// ** React Imports
import React, { useEffect, useState } from 'react'

// ** MUI Imports
import { Button, Grid, Typography } from '@mui/material'

// ** Child Imports
import DropdownButton from 'src/components/common/form-elements/DropdownButton'
import ChartComponent from '../visualizations/ChartComponent'
import AnalyticsDataWidget from '../visualizations/AnalyticsDataWidget'

// ** Helpers
import { ConversionUnitType, EmbededLinkType, WidgetType } from 'src/lib/types/dashboardAnalytics'
import { appliedDateFilter, arrayToCsv, downloadCsvHelper } from 'src/lib/utils/helpers'
import { conversionUnitOptions } from 'src/assets/AnalyticsConfig'
import { mapPaymentMode } from 'src/lib/utils/analyticsHelpers'
import { useFilter } from 'src/lib/contexts/FilterContext'
import { analyticsConstants } from 'src/lib/utils/constants'
import router from 'next/router'
import Link from 'next/link'
import { ArrowRight } from 'react-feather'
import { ConsoleNetworkOutline } from 'mdi-material-ui'

const PaymentSplit: React.FC<WidgetType> = ({ 
    id,
    value,
    isLoading,
    triggerDownloadReport,
    downloadTriggered,
    embededLinks,
    maxBarThickness=36
}) => {

    const { analyticsFilters } = useFilter();
    const [ selectedConversionUnit, setSelectedConversionUnit ] = useState<ConversionUnitType>("percentage");
    
    const plotData = value?.find((plotArray: any) => plotArray?.dimension?.includes(selectedConversionUnit))?.data;
    const reportData = plotData?.map((pointData: any, index: number) => {
        return [pointData?.x, pointData?.y]
    })

    const colors = ['#91C1E3', '#EFD080', '#BBE599', '#CF93EB', '#E49B9B', '#8A89DF', '#858585', '#B6B6BC'];

    const getPrepaidShare = () => {
        let prepaidShare = 0;
        plotData?.forEach((pointData: any, index: number) => {
            if(pointData?.x !== "COD") {
                prepaidShare += pointData?.y;
            }
        })
        return prepaidShare?.toFixed(2);
    }


    useEffect(() => {
        if(downloadTriggered) {
            downloadCsvHelper(arrayToCsv(reportData, ["Payment mode", selectedConversionUnit]), `payment_split_${selectedConversionUnit}.csv`);
            typeof triggerDownloadReport !== "undefined" && triggerDownloadReport(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [downloadTriggered])

    const dataSets = {
        labels: ["Payments"],
        datasets: plotData?.map((pointData: any, index: number) => {
            return {
                label: mapPaymentMode(pointData?.x, true, pointData?.y, selectedConversionUnit),
                backgroundColor: colors[index],
                data: [pointData?.y],
                pointHitRadius: 20,
                borderRadius: 6,
                maxBarThickness: maxBarThickness
            } 
        })
    }

    const handleUnitDropdownClick = (index: number) => {
        setSelectedConversionUnit(conversionUnitOptions[index].value as
            ConversionUnitType
        );
    }

    const fontProperties = {
        fontSize: 28,
        fontWeight: 600,
        fontColor: "#4D4D4D",
        lineHeight: '36px'
    }

    const renderWidgetContent = () => {
        switch (id) {
            case `${analyticsConstants.FLO_PAYMENT_SPLIT}_prepaid_share`:   
                return (
                    <div className='flex flex-col space-y-6 items-start'>
                        <AnalyticsDataWidget
                            widgetType={"summary"} 
                            valueType={selectedConversionUnit} 
                            widgetWidth={"33%"}
                            value={getPrepaidShare() ?? "0"}
                            isLoading={isLoading}
                            hideBorders
                            removeInnerPadding
                            cardVariant='outlined'
                            fontProperties={fontProperties}
                        />
                        { embededLinks?.map((link: EmbededLinkType, index: number) => {
                            return (
                                <Link key={`${link?.value}_${index}`} href={link?.value} passHref>
                                    <span className='decoration-2 decoration-gray-200 underline underline-offset-2 font-semibold text-sm flex items-center gap-1 cursor-pointer'>{ link?.label } <ArrowRight className='w-4 h-4 stroke-[2.5px]'/></span>
                                </Link>
                            )
                        }) }
                    </div>
                )
               
            case analyticsConstants.FLO_PAYMENT_SPLIT:
                return ( 
                    <div
                        style={{
                            width: "100%",
                            minHeight: "180px",
                            display: 'flex',
                            justifyContent: "start",
                            alignItems: "start",
                            flexDirection: "column"
                        }}
                    >
                        <div>
                            <DropdownButton
                                options={conversionUnitOptions}
                                handleClick={handleUnitDropdownClick}
                                variant="outlined"
                            />
                        </div>
                        <div style={{ minHeight: "380px", width: "100%" }}> 
                            <ChartComponent
                                chartType={"catagory_chart"}
                                data={dataSets}
                                selectedConversionUnit={selectedConversionUnit}
                                isLoading={isLoading}
                            /> 
                        </div>    
                    </div>
                ) 
            default: <></>
        }
    }
    
    return (
        <Grid 
            width={"100%"}
            sx={{  
                display:"flex",
                flexDirection: "column",
            }} 
        >
            {renderWidgetContent()}    
        </Grid>
    )
}

export default PaymentSplit